import chroma from 'chroma-js';

// Color scales for choropleth
export const colorScales = {
  // Default: cyan/teal sequential
  default: ['#0c4a6e', '#0369a1', '#0891b2', '#22d3ee', '#67e8f9', '#a5f3fc'],
  
  // High contrast version
  highContrast: ['#1e1b4b', '#4338ca', '#7c3aed', '#c084fc', '#e879f9', '#f5d0fe'],
  
  // Colorblind-safe (viridis-inspired)
  colorblindSafe: ['#440154', '#3b528b', '#21918c', '#5ec962', '#addc30', '#fde725'],
  
  // Warm (for high values emphasis)
  warm: ['#7f1d1d', '#b91c1c', '#dc2626', '#f97316', '#fbbf24', '#fef08a'],
};

/**
 * Create a color scale function for allocation values
 */
export function createColorScale(
  min: number,
  max: number,
  palette: keyof typeof colorScales = 'default'
): (value: number) => string {
  const colors = colorScales[palette];
  const scale = chroma.scale(colors).domain([min, max]).mode('lch');
  
  return (value: number) => {
    if (value === 0) return '#1e293b'; // surface-800 for no data
    return scale(value).hex();
  };
}

/**
 * Create breakpoints for legend
 */
export function createBreakpoints(min: number, max: number, steps = 5): number[] {
  const breakpoints: number[] = [];
  const step = (max - min) / steps;
  
  for (let i = 0; i <= steps; i++) {
    breakpoints.push(min + step * i);
  }
  
  return breakpoints;
}

/**
 * Get color for percent change
 */
export function getChangeColor(percentChange: number): string {
  if (percentChange > 50) return '#22c55e'; // success-500
  if (percentChange > 20) return '#4ade80'; // success-400
  if (percentChange > 0) return '#86efac'; // success-300
  if (percentChange === 0) return '#94a3b8'; // surface-400
  if (percentChange > -20) return '#fca5a5'; // red-300
  if (percentChange > -50) return '#f87171'; // danger-400
  return '#ef4444'; // danger-500
}

/**
 * Get rank color (1st, 2nd, 3rd special colors)
 */
export function getRankColor(rank: number): string {
  switch (rank) {
    case 1: return '#fbbf24'; // amber/gold
    case 2: return '#94a3b8'; // silver
    case 3: return '#d97706'; // bronze
    default: return '#64748b'; // muted
  }
}

/**
 * Interpolate between two colors
 */
export function interpolateColor(
  color1: string,
  color2: string,
  t: number
): string {
  return chroma.mix(color1, color2, t, 'lch').hex();
}

/**
 * State highlight colors for comparison mode
 */
export const stateColors = [
  '#22d3ee', // cyan
  '#f472b6', // pink
  '#a78bfa', // purple
  '#34d399', // emerald
  '#fbbf24', // amber
];

/**
 * Get color for state in comparison
 */
export function getStateColor(index: number): string {
  return stateColors[index % stateColors.length];
}

